import axios from 'axios';
import config from '../config';

/**
 * createEmiSale — EMI ya regular sale backend par POST karta hai.
 * Inventory.js aur doosri jagah se use ho sakta hai.
 *
 * @returns {{ success: boolean, message?: string, error?: string, similarCustomers?: Array }}
 */
export const createEmiSale = async (productId, saleData, selectedCustomer, newCustomer) => {
  try {
    if (!productId) throw new Error('Product ID is required');

    const { quantity, salePrice, date, isEmi, emiDetails, witness, useRewardPoints, rewardPointsToUse } = saleData;
    console.log(`[DEBUG] Creating ${isEmi ? 'EMI' : 'regular'} sale for product ${productId}`);

    if (!salePrice || parseFloat(salePrice) <= 0) {
      throw new Error('Please enter a valid sale price');
    }

    const productResponse = await axios.get(`${config.API_URL}/products/${productId}`);
    const product = productResponse.data;

    if (!product) {
      throw new Error('Product not found');
    }

    if (isEmi) {
      if (!emiDetails.downPayment || parseFloat(emiDetails.downPayment) <= 0) {
        throw new Error('Down payment is required for EMI sales');
      }
      if (parseFloat(emiDetails.downPayment) >= parseFloat(salePrice)) {
        throw new Error('Down payment cannot be greater than or equal to the sale price');
      }
      if (!saleData.customerName || !saleData.customerPhone) {
        throw new Error('Customer information is required for EMI sales');
      }
    }

    let customer = null;
    if (saleData.customerPhone || saleData.customerName) {
      try {
        const customerData = {
          phone: saleData.customerPhone,
          name: saleData.customerName || 'Customer',
          email: saleData.customerEmail || '',
          address: newCustomer?.address || '',
          idProof: newCustomer?.idProof || ''
        };

        if (selectedCustomer) {
          customer = selectedCustomer;
          console.log(`[DEBUG] Using selected customer: ${customer.name} (${customer._id})`);

          if (
            (newCustomer?.address && !selectedCustomer.address) ||
            (newCustomer?.idProof && !selectedCustomer.idProof)
          ) {
            await axios.patch(`${config.API_URL}/customers/${customer._id}`, {
              address: newCustomer?.address || selectedCustomer.address,
              idProof: newCustomer?.idProof || selectedCustomer.idProof
            });
          }
        } else {
          const response = await axios.post(`${config.API_URL}/customers/find-or-create`, customerData);
          if (response.data.similar) {
            return {
              success: false,
              error: 'Similar customers found',
              similarCustomers: response.data.customers
            };
          }
          customer = response.data.customer;
          console.log(`[DEBUG] Created/found customer: ${customer.name} (${customer._id})`);
        }
      } catch (err) {
        console.error('Error finding/creating customer:', err);
        if (err.message !== 'Similar customers found. Please select one or create a new customer.') {
          throw err;
        }
        return { success: false, error: err.message, similarCustomers: err.similarCustomers || null };
      }
    }

    const transactionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    let saleId = null;

    if (isEmi && customer) {
      console.log(`[DEBUG] Creating EMI sale with transactionId: ${transactionId}`);

      const originalSalePrice = parseFloat(salePrice);
      let pointsUsed = 0;
      if (useRewardPoints && rewardPointsToUse > 0) {
        const maxPoints = Math.min(
          customer.rewardPoints || 0,
          Math.floor(originalSalePrice)
        );
        pointsUsed = Math.min(rewardPointsToUse || 0, maxPoints);
      }
      const effectiveSalePrice = Math.max(0, originalSalePrice - pointsUsed);

      const principal = effectiveSalePrice - parseFloat(emiDetails.downPayment);
      const interestRate = emiDetails.interestRate ? parseFloat(emiDetails.interestRate) : 0;
      const interest = (principal * interestRate * emiDetails.months) / 100;
      const totalAmount = principal + interest;
      const monthlyAmount = totalAmount / emiDetails.months;

      const emiApplication = {
        product: product._id,
        customer: {
          name: customer.name,
          email: customer.email || '',
          phone: customer.phone,
          address: customer.address || '',
          idProof: customer.idProof || ''
        },
        variant:
          product.variants && product.variants.length > 0
            ? `${product.variants[0].name}: ${product.variants[0].value}`
            : '',
        useRewardPoints: pointsUsed > 0,
        rewardPointsToUse: pointsUsed,
        customerId: customer._id,
        saleInfo: {
          salePrice: originalSalePrice,
          saleDate: new Date(date),
          transactionId,
          witness:
            witness && (witness.id || witness.phone)
              ? {
                  witnessId: witness.id || null,
                  witnessName: witness.name || '',
                  witnessPhone: witness.phone || ''
                }
              : undefined
        },
        emiPlan: {
          months: parseInt(emiDetails.months),
          interestRate: interestRate,
          downPayment: parseFloat(emiDetails.downPayment),
          preferredPaymentDate: parseInt(emiDetails.preferredPaymentDate),
          principal: principal.toFixed(2),
          monthlyAmount: monthlyAmount.toFixed(2),
          totalAmount: (totalAmount + parseFloat(emiDetails.downPayment)).toFixed(2)
        },
        imeiIndex: saleData.selectedImeiIndex !== null ? saleData.selectedImeiIndex : undefined
      };

      console.log(`[DEBUG] Sending EMI application to server`);
      const emiResponse = await axios.post(`${config.API_URL}/emi`, emiApplication);

      if (emiResponse.data && emiResponse.data._id) {
        saleId = emiResponse.data._id;
        console.log(`[DEBUG] EMI created with ID: ${saleId}`);
        console.log(`[DEBUG] EMI sale complete - NOT adding purchase history (backend does this)`);
      }
    } else {
      console.log(`[DEBUG] Creating regular sale with transactionId: ${transactionId}`);

      const saleRecord = {
        quantity: parseInt(quantity),
        salePrice: parseFloat(salePrice),
        customerName: saleData.customerName || '',
        customerPhone: saleData.customerPhone || '',
        date: new Date(date),
        transactionId,
        useRewardPoints: useRewardPoints || false,
        rewardPointsToUse: rewardPointsToUse || 0
      };

      if (customer && customer._id) {
        saleRecord.customerId = customer._id;
      }

      if (saleData.selectedImeiIndex !== null && saleData.selectedImeiIndex !== undefined) {
        saleRecord.selectedImeiIndex = saleData.selectedImeiIndex;
      }

      console.log(`[DEBUG] Sending sale record to server`);
      const saleResponse = await axios.post(`${config.API_URL}/products/${productId}/sales`, saleRecord);

      if (saleResponse.data) {
        const sales = saleResponse.data.sales || [];
        const lastSale = sales.length ? sales[sales.length - 1] : null;
        saleId = lastSale?._id || saleResponse.data._id;
        console.log(`[DEBUG] Sale created, productSaleId: ${saleId}`);
        if (customer && customer._id && !isEmi) {
          console.log(`[DEBUG] Cash sale recorded - backend will handle purchase history automatically`);
        }
      }
    }

    console.log(`[DEBUG] Sale process completed successfully`);
    let productSaleId = null;
    if (!isEmi && saleId) {
      productSaleId = saleId;
    } else if (!isEmi) {
      /* saleId may be unset if API shape differs — caller can still upload without link */
    }
    return {
      success: true,
      message: isEmi ? 'EMI sale recorded successfully' : 'Sale recorded successfully',
      emiId: isEmi && saleId ? saleId : null,
      productId,
      productSaleId: !isEmi ? productSaleId : null,
      customerId: customer?._id || null
    };
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'An unexpected error occurred';
    console.error('[DEBUG] Error in createEmiSale:', err.response?.data || err);
    return { success: false, error: errorMessage, similarCustomers: err.similarCustomers || null };
  }
};
